"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Search } from "lucide-react";
import { useRouter } from "next/navigation";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useTransition,
} from "react";
import { useForm, useWatch } from "react-hook-form";

import { TextShimmer } from "@/components/text-shimmer";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { baseApi } from "@/redux/api/base-api";
import { useAppDispatch } from "@/redux/hooks";
import { IVisaSearchSchema } from "../../../search/types/visa.types";
import { encodedVisaParams } from "../../lib/visa-formatter-helper";
import {
  getLastVisaSearch,
  saveLastVisaSearch,
} from "../../params/visa-search-persistence";
import { visaSearchSchema } from "../../schema/visa-search-zod.schema";
import { VisaFields } from "./visa-fields";

export const defaultVisaFormValue: IVisaSearchSchema = {
  country: null,
  visa_type: null,
  travelers: 1,
};

interface VisaSearchFormProps {
  searchData?: IVisaSearchSchema;
  setOpen?: Dispatch<SetStateAction<boolean>>;
}

export function VisaSearchForm({ searchData, setOpen }: VisaSearchFormProps) {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [isPending, startTransition] = useTransition();

  const formValues = useMemo(() => {
    return searchData ?? getLastVisaSearch() ?? defaultVisaFormValue;
  }, [searchData]);

  const form = useForm<IVisaSearchSchema>({
    resolver: zodResolver(visaSearchSchema),
    values: formValues,
  });

  const travelers = useWatch({
    control: form.control,
    name: "travelers",
    defaultValue: formValues.travelers,
  });

  const onSubmit = useCallback(
    (data: IVisaSearchSchema) => {
      saveLastVisaSearch(data);

      startTransition(() => {
        dispatch(baseApi.util.invalidateTags(["VISA_SEARCH"]));
        const params = encodedVisaParams(data);
        router.push(params ? `/visa?${params}` : "/visa", { scroll: false });
        setOpen?.(false);
      });
    },
    [router, setOpen, dispatch],
  );

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="h-full w-full">
      <div className="mb-5  flex flex-wrap items-center gap-2 md:gap-4">
        <VisaFields.SearchType />
        <VisaFields.VisaType form={form} />
        <VisaFields.Travelers totalTravelers={travelers ?? 1} />
      </div>

      <div
        className={cn(
          "grid items-start gap-3",
          "grid-cols-1 md:grid-cols-[minmax(0,1.2fr)_repeat(2,minmax(0,1fr))_52px] xl:grid-cols-[minmax(0,1.2fr)_repeat(2,minmax(0,1fr))_56px]",
        )}
      >
        <VisaFields.Country form={form} />
        <VisaFields.Type form={form} />
        <VisaFields.Traveler form={form} />

        <Button
          type="submit"
          className="h-12 rounded-md bg-primary px-4 text-primary-foreground hover:bg-primary/90 md:size-13 md:min-w-13 md:px-0 xl:size-14 xl:min-w-14"
          size="lg"
          disabled={isPending}
          aria-label="Search visa"
        >
          <Search className="h-5 w-5" />
          <span className="md:hidden">
            {isPending ? (
              <TextShimmer duration={1.2}>Search</TextShimmer>
            ) : (
              "Search"
            )}
          </span>
        </Button>
      </div>
    </form>
  );
}
